import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Pagination, 
  TextField,
  Skeleton,
} from "@mui/material";
import { useParams } from "react-router-dom";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import api from "../../services/api/api";

const normalizarTexto = (texto) => {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

const calcularDias = (dataReferencia) => {
  const dataAtual = new Date();
  const data = new Date(dataReferencia);
  const diferencaMs = dataAtual - data;
  return Math.floor(diferencaMs / (1000 * 60 * 60 * 24));
};

// Formatar data para o formato dd/MM/yyyy
const formatarData = (dataReferencia) => {
  const dataObj = new Date(dataReferencia);
  const dia = String(dataObj.getDate()).padStart(2, "0");
  const mes = String(dataObj.getMonth() + 1).padStart(2, "0");
  const ano = dataObj.getFullYear();
  return `${dia}/${mes}/${ano}`;
};

// Componente de Loading Skeleton
const PublicacaoSkeleton = () => {
  const theme = useTheme();
  
  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            padding: "10px",
            textAlign: "center",
            background: theme.palette.grey[200],
            borderRadius: "10px",
            marginBottom: 2
          }}
        >
          <Skeleton variant="rectangular" width={50} height={50} sx={{ margin: "0 auto", marginBottom: 1 }} />
          <Skeleton variant="text" width="60%" sx={{ margin: "0 auto" }} />
        </Box>
        <Skeleton variant="text" width="90%" height={24} sx={{ marginBottom: 1 }} />
        <Skeleton variant="text" width="70%" height={16} sx={{ marginBottom: 0.5 }} />
        <Skeleton variant="text" width="60%" height={16} />
      </CardContent>
    </Card>
  );
};

// Componente de Estado Vazio
const EstadoVazio = ({ onTentarNovamente, isSearchResult = false }) => {
  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      alignItems: "center", 
      justifyContent: "center",
      minHeight: "300px",
      textAlign: "center",
      padding: 4
    }}>
      <SearchOffIcon sx={{ fontSize: 80, color: "text.secondary", marginBottom: 2 }} />
      <Typography variant="h5" sx={{ marginBottom: 1, fontWeight: 600 }}>
        {isSearchResult ? "Nenhuma publicação encontrada" : "Nenhuma publicação disponível"}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ marginBottom: 3, maxWidth: 400 }}>
        {isSearchResult 
          ? "Não encontramos publicações que correspondam aos critérios de busca. Tente ajustar os filtros ou limpar a pesquisa."
          : "No momento não há publicações disponíveis. Tente novamente mais tarde."
        }
      </Typography>
      <Button
        variant="contained"
        startIcon={<RefreshIcon />}
        onClick={onTentarNovamente}
        size="large"
      >
        Tentar Novamente
      </Button>
    </Box>
  );
};

const Publicacoes = () => {
  const theme = useTheme();
  const { categoria } = useParams();
  const [publicacoes, setPublicacoes] = useState([]);
  const [filteredPublicacoes, setFilteredPublicacoes] = useState([]);
  const [page, setPage] = useState(1);
  const [periodo, setPeriodo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const publicacoesPorPagina = 20;

  const fetchPublicacoes = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/getAllPublications");
      // Filtrar publicados e ordenar por data decrescente (mais recentes primeiro)
      const publicados = response.data
        .filter((pub) => pub.isPublished)
        .sort((a, b) => new Date(b.publicationDate) - new Date(a.publicationDate));
      setPublicacoes(publicados);

      // Filtrar inicialmente pela categoria da URL
      const iniciais = categoria
        ? publicados.filter((pub) => pub.category === categoria)
        : publicados;
      setFilteredPublicacoes(iniciais);
    } catch (error) {
      console.error("Erro ao buscar publicações:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicacoes();
  }, [categoria]);

  const handleSearch = () => {
    setHasSearched(true);
    // Base: apenas publicações da categoria da URL (se existir)
    const base = categoria
      ? publicacoes.filter((pub) => pub.category === categoria)
      : publicacoes;

    let filtros = base.filter((publicacao) => {
      const dataNoticia = new Date(publicacao.publicationDate);
      const anoMesNoticia = `${dataNoticia.getFullYear()}-${(
        dataNoticia.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}`;
      const correspondePeriodo = !periodo || anoMesNoticia === periodo;
      const correspondeDescricao =
        !descricao || normalizarTexto(publicacao.name).includes(normalizarTexto(descricao));
      return correspondePeriodo && correspondeDescricao;
    });

    // Ordena também os filtros por data decrescente
    filtros = filtros.sort((a, b) => new Date(b.publicationDate) - new Date(a.publicationDate));
    setFilteredPublicacoes(filtros);
    setPage(1);
  };

  const handleClear = () => {
    setPeriodo("");
    setDescricao("");
    setHasSearched(false);
    // Ao limpar, manter apenas categoria da URL
    const iniciais = categoria
      ? publicacoes.filter((pub) => pub.category === categoria)
      : publicacoes;
    setFilteredPublicacoes(iniciais);
    setPage(1);
  };

  const handleTentarNovamente = () => {
    setHasSearched(false);
    fetchPublicacoes();
  };

  const totalPaginas = Math.ceil(filteredPublicacoes.length / publicacoesPorPagina);
  const publicacoesPaginadas = filteredPublicacoes.slice(
    (page - 1) * publicacoesPorPagina,
    page * publicacoesPorPagina
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRedirect = (file) => {
    window.location.href = file;
  };

  // Mostrar skeletons durante o loading
  if (loading) {
    return (
      <Box sx={{ padding: 4, width: { xs: "95%", md: "70%" } }}>
        <Grid container spacing={2} sx={{ marginBottom: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton variant="rectangular" height={56} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Skeleton variant="rectangular" height={56} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Skeleton variant="rectangular" height={56} width={200} />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {[1, 2, 3].map((index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <PublicacaoSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 4, width: { xs: "95%", md: "70%" } }}>
      <Grid container spacing={2} sx={{ marginBottom: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Mês e Ano"
            type="month"
            fullWidth
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Título da publicação"
            fullWidth
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button variant="contained" onClick={handleSearch} sx={{ marginRight: 2 }}>
            Pesquisar
          </Button>
          <Button variant="outlined" onClick={handleClear}>
            Limpar
          </Button>
        </Grid>
      </Grid>

      {filteredPublicacoes.length === 0 ? (
        <EstadoVazio 
          onTentarNovamente={handleTentarNovamente}
          isSearchResult={hasSearched || Boolean(periodo || descricao)}
        />
      ) : (
        <>
          <Grid container spacing={2}>
            {publicacoesPaginadas.map((publicacao) => (
              <Grid item xs={12} sm={6} md={4} key={publicacao._id}>
                <Card>
                  <CardContent sx={{ cursor: "pointer" }} onClick={() => handleRedirect(publicacao.file)}>
                    <Box
                      sx={{
                        padding: "10px",
                        textAlign: "center",
                        background: theme.palette.primary.main,
                        color: theme.palette.primary.text,
                        borderRadius: "10px",
                      }}
                    >
                      <PdfIcon sx={{ fontSize: "50px" }} />
                      <Typography sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                        {publicacao.category}
                      </Typography>
                    </Box>
                    <Typography sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                      {publicacao.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Publicado em: {formatarData(publicacao.publicationDate)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Há {calcularDias(publicacao.publicationDate)} dias atrás
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
            <Pagination count={totalPaginas} page={page} onChange={handlePageChange} color="primary" />
          </Box>
        </>
      )}
    </Box>
  );
};

export default Publicacoes;